import { Edit, InfoOutlined as InfoOutlinedIcon } from '@mui/icons-material'
import { Tooltip } from '@mui/material'
import { HolidayHourResource, HolidayHourTimePeriod } from 'api/models'
import { createHolidayHours, deleteHolidayHours, getHolidayHours } from 'api/partners/holidayHours'
import { LoadingSkeleton } from 'components'
import useGetLocationPartners from 'hooks/useGetLocationPartners'
import moment from 'moment'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { convertUTCToPartnerTimezone, getTzValueByKey } from 'utils/DateTime'

import {
  SectionContent,
  SectionEditIcon,
  SectionHeader,
  SectionTitle,
  SectionTitleContainer,
  SectionWrapper,
} from './../styles'
import HolidayHoursForm from './HolidayHoursForm'
import HolidayHoursList from './HolidayHoursList'

export type HolidayHour = {
  id: string
  date: string
  closed: boolean
  timePeriod?: { startTime: string; endTime: string }
}

export type HolidayHourWithTimePeriods = HolidayHourResource & {
  time_period?: HolidayHourTimePeriod
}

type Props = {
  partnerId: string
  isEditing: boolean
  setIsEditing: (isEditing: boolean) => void
}

const HolidayHours = ({ partnerId, isEditing, setIsEditing }: Props) => {
  const [holidayHours, setHolidayHours] = useState<HolidayHourWithTimePeriods[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const { partner } = useGetLocationPartners(partnerId)
  const partnerTZ = partner?.attributes.timezone || ''
  const timezone = getTzValueByKey(partnerTZ)
  // if partnerTimezone is null, use browser timezone.
  const partnerTimezone = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
  const isPending = isLoading || isSaving || (isEditing && holidayHours.length === 0)

  const fetchHolidayHours = useCallback(async () => {
    try {
      if (!partnerId) return
      setIsLoading(true)
      const response = await getHolidayHours(partnerId)

      // Add time periods if a relationship exists
      const datesWithPeriods = response.data.map((hour) => {
        if (hour.relationships.time_periods.data.length > 0) {
          const firstTimePeriodId = hour.relationships.time_periods.data[0].id
          const firstTimePeriod = response.included?.find(
            (period) => period.id === firstTimePeriodId,
          )
          return {
            ...hour,
            ...(firstTimePeriod && { time_period: firstTimePeriod.attributes }),
          }
        }
        return hour
      })

      // Sort by date
      datesWithPeriods.sort((a, b) =>
        moment(a.attributes.date).isBefore(moment(b.attributes.date)) ? -1 : 1,
      )
      setHolidayHours(datesWithPeriods)
    } catch (error) {
      console.error('Failed to fetch holiday hours:', error)
    } finally {
      setIsLoading(false)
    }
  }, [partnerId])

  const saveHolidayHours = async (updatedHours: HolidayHour[]) => {
    setIsSaving(true)
    try {
      const original = holidayHours.map((hour) => {
        const date = moment(
          convertUTCToPartnerTimezone(hour.attributes.date, partnerTimezone),
        ).format('YYYY-MM-DD')
        return {
          id: hour.id,
          date,
          closed: hour.attributes.closed,
          timePeriod: hour.relationships.time_periods?.data.length
            ? {
                startTime: hour.time_period?.start_time || '',
                endTime: hour.time_period?.end_time || '',
              }
            : undefined,
        }
      })

      const originalIds = original.map((o) => o.id)

      const newHours = updatedHours.filter((hour) => !originalIds.includes(hour.id))

      const deletedIds = originalIds.filter((id) => !updatedHours.some((hour) => hour.id === id))

      const editedHours = updatedHours.filter((hour) => {
        const originalMatch = original.find((o) => o.id === hour.id)
        if (!originalMatch) return false

        const hasChanged =
          hour.date !== originalMatch.date ||
          hour.closed !== originalMatch.closed ||
          hour.timePeriod?.startTime !== originalMatch.timePeriod?.startTime ||
          hour.timePeriod?.endTime !== originalMatch.timePeriod?.endTime

        return hasChanged
      })

      // Delete removed + edited entries first
      const idsToDelete = [...deletedIds, ...editedHours.map((h) => h.id)]
      await Promise.all(idsToDelete.map((id) => deleteHolidayHours(partnerId, id)))

      // Re-create edited + new entries
      const toCreate = [...newHours, ...editedHours]

      await Promise.all(
        toCreate.map((hour) => {
          const payload = {
            data: {
              attributes: {
                date: moment(hour.date).format('YYYY-MM-DD'),
                closed: hour.closed,
              },
              ...(hour.timePeriod &&
                !hour.closed &&
                hour.timePeriod.startTime &&
                hour.timePeriod.endTime && {
                  time_periods_attributes: [
                    {
                      start_time: moment(hour.timePeriod.startTime, ['HH:mm', 'h:mm A']).format(
                        'HH:mm',
                      ),
                      end_time: moment(hour.timePeriod.endTime, ['HH:mm', 'h:mm A']).format(
                        'HH:mm',
                      ),
                    },
                  ],
                }),
            },
          }
          return createHolidayHours(partnerId, payload)
        }),
      )

      toast.success('Holiday hours saved!', { autoClose: 5000 })
      fetchHolidayHours()
      setIsEditing(false)
    } catch (error) {
      console.error('Failed to save holiday hours:', error)
    } finally {
      setIsSaving(false)
    }
  }

  useEffect(() => {
    if (!partnerId) return
    fetchHolidayHours()
  }, [partnerId, fetchHolidayHours])

  return (
    <SectionWrapper isEditingMode={isEditing}>
      <SectionHeader>
        <SectionTitleContainer>
          <SectionTitle>Holiday Hours</SectionTitle>
          <Tooltip
            title='This will update your online ordering platforms to no longer accept orders and will update your "Special Hours" on your Google Business Profile.'
            arrow
          >
            <InfoOutlinedIcon
              style={{
                marginLeft: '8px',
                color: '#777777',
                fontSize: '20px',
                cursor: 'pointer',
              }}
            />
          </Tooltip>
        </SectionTitleContainer>
        {!isPending && !isEditing && (
          <Tooltip title='Edit holiday hours' arrow>
            <SectionEditIcon onClick={() => setIsEditing(true)}>
              <Edit />
            </SectionEditIcon>
          </Tooltip>
        )}
      </SectionHeader>
      <SectionContent>
        <SectionContent>
          {isPending ? (
            <LoadingSkeleton isFlex />
          ) : (
            <>
              {isEditing ? (
                <HolidayHoursForm
                  holidayHours={holidayHours}
                  partnerTimezone={partnerTimezone}
                  saveButtonDisabled={isSaving}
                  onSave={saveHolidayHours}
                  onCancel={() => setIsEditing(false)}
                />
              ) : (
                <HolidayHoursList
                  holidayHours={holidayHours}
                  partnerTimezone={partnerTimezone}
                />
              )}
            </>
          )}
        </SectionContent>
      </SectionContent>
    </SectionWrapper>
  )
}

export default HolidayHours
